import React from "react";

// Presentation-only helper for pagination rows that used to render one button
// per page (80+ buttons on big lists).
//
// Call sites keep their own pagination state and handlers untouched: they still
// build the full list of page elements (<Pagination.Item>, <li>, <button>)
// exactly as before and just pass it through here together with the current
// page number. This only decides which of those existing elements are visible
// and inserts "..." between the gaps:
//
//   page 1  of 84:  1 2 3 4 5 ... 84
//   page 42 of 84:  1 ... 40 41 42 43 44 ... 84
//   page 84 of 84:  1 ... 80 81 82 83 84
//
// First and last page are always shown, plus the current page and `siblings`
// pages either side (so at most 9 entries). A gap of a single page is shown as
// that page rather than as "...".

export const ELLIPSIS = "ellipsis";

// Pure page-range calculation: returns page numbers (1-based) with ELLIPSIS
// markers where ranges are skipped.
export function getCompactPageRange(currentPage, totalPages, siblings = 2) {
  const total = Math.max(Math.floor(Number(totalPages) || 0), 0);
  if (total === 0) return [];
  const current = Math.min(Math.max(Number(currentPage) || 1, 1), total);
  const windowSize = siblings * 2 + 1;

  let start = current - siblings;
  let end = current + siblings;
  if (start < 1) {
    start = 1;
    end = Math.min(total, windowSize);
  }
  if (end > total) {
    end = total;
    start = Math.max(1, total - windowSize + 1);
  }

  const keep = new Set([1, total]);
  for (let p = start; p <= end; p += 1) keep.add(p);
  // Replace a one-page gap with the page itself ("1 ... 3" -> "1 2 3").
  if (start === 3) keep.add(2);
  if (end === total - 2) keep.add(total - 1);

  const out = [];
  let previous = 0;
  [...keep]
    .sort((a, b) => a - b)
    .forEach((p) => {
      if (p - previous > 1) out.push(ELLIPSIS);
      out.push(p);
      previous = p;
    });
  return out;
}

const defaultEllipsis = (key) => (
  <li key={key} className="page-item disabled" aria-hidden="true">
    <span className="page-link">…</span>
  </li>
);

// `items[k]` must be the element for page k + 1 (as the old `.map` produced).
// Pass `renderEllipsis(key)` when the row is not a Bootstrap <ul>/<li> list
// (e.g. plain <button> rows).
export function compactPageItems(items, currentPage, { siblings = 2, renderEllipsis = defaultEllipsis } = {}) {
  if (!Array.isArray(items)) return items;
  return getCompactPageRange(currentPage, items.length, siblings).map((entry, i) =>
    entry === ELLIPSIS ? renderEllipsis(`ellipsis-${i}`) : items[entry - 1]
  );
}

export default compactPageItems;
